/**
 * StatusBadge Component
 * Displays health status with color-coded badge
 */

import React from 'react';
import { Tag } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import type { HealthStatus } from '../types';

interface StatusBadgeProps {
  status: HealthStatus;
  showLabel?: boolean;
  size?: 'small' | 'default' | 'large';
}

const statusConfig: Record<
  HealthStatus,
  {
    color: string;
    icon: React.ReactNode;
    label: string;
  }
> = {
  healthy: {
    color: 'success',
    icon: <CheckCircleOutlined />,
    label: 'Healthy',
  },
  degraded: {
    color: 'warning',
    icon: <WarningOutlined />,
    label: 'Degraded',
  },
  unhealthy: {
    color: 'error',
    icon: <CloseCircleOutlined />,
    label: 'Unhealthy',
  },
  unknown: {
    color: 'default',
    icon: <QuestionCircleOutlined />,
    label: 'Unknown',
  },
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  showLabel = true,
  size = 'default',
}) => {
  const config = statusConfig[status] || statusConfig.unknown;

  const fontSize = size === 'large' ? 14 : size === 'small' ? 11 : 12;

  return (
    <Tag
      icon={config.icon}
      color={config.color}
      style={{
        fontSize,
        padding: size === 'large' ? '4px 12px' : undefined,
      }}
    >
      {showLabel ? config.label : null}
    </Tag>
  );
};

export default StatusBadge;
